import {Inject, Injectable} from '@angular/core';
import {Observable, of, ReplaySubject} from "rxjs";
import {CountedAndPaginated} from "../interface/response/counted-and-paginated";
import {DobryAgentBlogTag} from "../interface/dobry-agent-blog-article";
import {HttpClient} from "@angular/common/http";
import {FilterService} from "./filter.service";
import {DOCUMENT} from "@angular/common";
import {first, map, switchMap, take, tap} from "rxjs/operators";
import {Uuid} from "../interface/response/uuid";
import {ConfirmDialogComponent} from "../components/confirm-dialog/confirm-dialog.component";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatDialog} from "@angular/material/dialog";

@Injectable({
  providedIn: 'root'
})
export class DobryAgentBlogTagService {
  public readonly blogTagListSubject: ReplaySubject<CountedAndPaginated<DobryAgentBlogTag>>
    = new ReplaySubject<CountedAndPaginated<DobryAgentBlogTag>>(1);

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    @Inject(DOCUMENT) private document: Document,
    private snack: MatSnackBar,
    private dialog: MatDialog,
  ) {
  }

  public loadList(page: number, pageSize: number, filters: object, sort?: { sortBy?: string; sortOrder?: string}): void {
    this.httpClient.get(`/rest/dobryagent/api/internal/blog-tag?page=${page + 1}&pageSize=${pageSize}&sortBy=${sort.sortBy ?? ''}&sortOrder=${sort.sortOrder ?? ''}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: CountedAndPaginated<DobryAgentBlogTag>) => {
        this.blogTagListSubject.next(result);
      });
  }


  public getBlogTagListSubject(): ReplaySubject<CountedAndPaginated<DobryAgentBlogTag>> {
    return this.blogTagListSubject;
  }

  public get(uuid: string): Observable<DobryAgentBlogTag> {
    return this.httpClient
      .get<DobryAgentBlogTag>(`/rest/dobryagent/api/internal/blog-tag/${uuid}`)
      .pipe(take(1));
  }

  public send(formData: object, uuid?: string): Observable<Uuid> {
    if (uuid) {
      return this.httpClient
        .put<Uuid>(`/rest/dobryagent/api/internal/blog-tag/${uuid}`, formData)
        .pipe(take(1));
    }

    return this.httpClient
      .post<Uuid>(`/rest/dobryagent/api/internal/blog-tag`, formData)
      .pipe(take(1));
  }

  public searchList(
    page: number,
    pageSize: number,
    filters: object = {},
    sort: { sortBy?: string; sortOrder?: string } = {}
  ): Observable<CountedAndPaginated<DobryAgentBlogTag>> {
    const query = this.filterService.parseFilters(filters);

    return this.httpClient
      .get<CountedAndPaginated<DobryAgentBlogTag>>(
        `/rest/dobryagent/api/internal/blog-tag` +
        `?page=${page + 1}` +
        `&pageSize=${pageSize}` +
        `&sortBy=${sort.sortBy ?? ''}` +
        `&sortOrder=${sort.sortOrder ?? ''}` +
        `&${query}`
      )
      .pipe(take(1));
  }


  private delete(uuid: string): Observable<void> {
    return this.httpClient
      .delete<void>(
        `/rest/dobryagent/api/internal/blog-tag/${uuid}/delete`
      )
      .pipe(first());
  }

  private deleteWeak(uuid: string): Observable<void> {
    return this.httpClient
      .patch<void>(
        `/rest/dobryagent/api/internal/blog-tag/${uuid}`,
        {}
      )
      .pipe(first());
  }


  private restore(uuid: string): Observable<void> {
    return this.httpClient
      .patch<void>(
        `/rest/dobryagent/api/internal/blog-tag/${uuid}/restore`,
        {}
      )
      .pipe(first());
  }

  public confirmDelete(
    blogTag: DobryAgentBlogTag
  ): Observable<boolean> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        text: 'Czy na pewno chcesz usunąć tag?'
      },
    });

    return dialogRef.afterClosed()
      .pipe(
        take(1),
        switchMap((confirmed: boolean) => {
          if (!confirmed) {
            return of(false);
          }

          return this.delete(blogTag.uuid)
            .pipe(
              tap(() => {
                this.snack.open(
                  'Pomyślnie usunięto tag',
                  '',
                  {duration: 2000}
                );
              }),
              map(() => true)
            );
        })
      );
  }

  public confirmDeleteWeak(
    uuid: string
  ): Observable<boolean> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        text: 'Czy na pewno chcesz przenieść tag do archiwum?'
      },
    });

    return dialogRef.afterClosed()
      .pipe(
        take(1),
        switchMap((confirmed: boolean) => {
          if (!confirmed) {
            return of(false);
          }

          return this.deleteWeak(uuid)
            .pipe(
              tap(() => {
                this.snack.open(
                  'Pomyślnie przeniesiono tag do archiwum',
                  '',
                  {duration: 2000}
                );
              }),
              map(() => true)
            );
        })
      );
  }


  public confirmRestore(
    blogTag: DobryAgentBlogTag
  ): Observable<boolean> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        text: 'Czy na pewno chcesz przywrócić tag z archiwum?'
      },
    });

    return dialogRef.afterClosed()
      .pipe(
        take(1),
        switchMap((confirmed: boolean) => {
          if (!confirmed) {
            return of(false);
          }

          return this.restore(blogTag.uuid)
            .pipe(
              tap(() => {
                this.snack.open(
                  'Pomyślnie przywrócono tag',
                  '',
                  {duration: 2000}
                );
              }),
              map(() => true)
            );
        })
      );
  }
}

